




























































import Vue, { PropOptions } from 'vue';

import SButton from '@stratumfive/ui-baseplate/src/components/SButton/SButton.vue';

import EventBus from '../../EventsBus';
import SubmissionInformation from './SubmissionInformation.vue';
import GroupField from '../../fields/components/GroupField.vue';
import { Form } from '../form';
import { Field } from '../../fields/field';
import { SubmissionGlobalValue } from '../../formulas/types/ComputerDefinition';
import {
  FormDefinition,
  ExistingSubmission,
  SubmissionVersion,
  DataOption,
  VesselGlobal,
  VesselConsumptionConfig,
  SharedGlobal,
  FormSubmission,
  Warning,
} from '../types/FormDefinition';

/**
 * The highest level component of a Snapshot Form that takes in an array
 * of fields and a date, and then will generate Field and Computer instances.
 */
export default Vue.extend({
  name: 'SnapshotForm',

  components: {
    SButton,
    SubmissionInformation,
    GroupField,
  },

  props: {
    showEdit: {
      type: Boolean,
      required: false,
      default: true,
    },
    schema: {
      type: Object,
      required: true,
    } as PropOptions <FormDefinition>,
    existingSubmission: {
      type: Object,
      required: false,
    } as PropOptions <ExistingSubmission>,
    globals: {
      type: Array,
      required: false,
      default: () => [],
    } as PropOptions <SubmissionGlobalValue[]>,
    sharedGlobals: {
      type: Array,
      required: false,
      default: () => [],
    } as PropOptions <SharedGlobal[]>,
    vesselGlobals: {
      type: Array,
      required: false,
      default: () => [],
    } as PropOptions <VesselGlobal[]>,
    vesselConsumptionConfig: {
      type: Array,
      required: false,
      default: () => [],
    } as PropOptions <VesselConsumptionConfig[]>,
    dataOptions: {
      type: Array,
      required: false,
      default: () => [],
    } as PropOptions <DataOption[]>,
    versions: {
      type: Array,
      required: false,
      default: () => [],
    } as PropOptions <SubmissionVersion[]>,
    editable: {
      type: Boolean,
      required: false,
      default: true,
    },
    authorName: {
      type: String,
      required: false,
      default: '',
    },
    vessel: {
      type: Object,
      required: false,
      default: () => {},
    },
    options: {
      type: Object,
      required: false,
      default: () => ({
        allowSubmit: true,
        allowSaveAsDraft: true,
        allowDuplicateTimestamps: true,
      }),
    },
    previousSubmissionDate: {
      type: String,
      required: false,
      default: '',
    },
    previousSubmissions: {
      type: Array,
      required: false,
      default: () => [],
    } as PropOptions<ExistingSubmission[]>,
    /**
     * Set this to true to have the submission generated
     * and returned via and @update event evertime its
     * updated
     */
    listenForUpdates: {
      type: Boolean,
      required: false,
      default: false,
    },
    deletable: {
      type: Boolean,
      default: false,
    },
  },

  provide(): any {
    return {
      dataOptions: () => this.dataOptions,
      allFields: () => this.form.state.fields,
      editable: () => this.form.state.editable,
      currentVersion: () => this.form.state.currentVersion,
      latestVersion: () => this.form.state.latestVersion,
    };
  },

  data(): any {
    return {
      form: null,
      formMeta: null,
    };
  },

  computed: {
    /**
     * As each section of a form will exist inside of a `group` field,
     * we only want to try and render groups at this top level.
     */
    formGroups(): Field[] {
      if (!this.form?.state?.fields) return [];
      return this.form.formDef.children
        .map((id) => this.form.state.fields.find((field) => field.id === id));
    },
    /**
     * Title for the entire form.
     */
    submissionTitle(): string {
      return this.form.formDef.title ? this.form.formDef.title : 'Submission';
    },
    /**
     * Get if the form is editable
     */
    formIsEditable(): boolean {
      return this.form.state.editable;
    },
    /**
     * If the contents of the form should be visible.
     */
    formIsOpen(): boolean {
      return this.form.state.formIsOpen;
    },
  },

  watch: {
    editable(canEdit: boolean) {
      this.form.setEditable(canEdit);
    },

    previousSubmissionDate(newDate) {
      this.form.setPreviousSubmissionDate(newDate);
    },
  },

  created() {
    this.initialiseForm();
  },

  methods: {

    /**
     * This will simply take the props of this component and create a new
     * instance of a `Form` class.
     */
    initialiseForm(): void {
      const {
        schema,
        sharedGlobals,
        globals,
        vesselGlobals,
        vesselConsumptionConfig,
        authorName,
        vessel,
        existingSubmission,
        versions,
      } = this;

      this.form = new Form({
        formDef: schema,
        sharedGlobals,
        globals,
        vesselGlobals,
        vesselConsumptionConfig,
        authorName,
        vessel,
        existingSubmission,
        submissionVersions: versions,
        onUpdate: this.listenForUpdates ? this.onUpdate : null,
      });

      this.allFields = this.form.state.fields;

      EventBus.$on('dateSet', (value) => this.$emit('dateSet', value));

      if (!this.editable) {
        this.form.setEditable(false);
      }
    },
    /**
     * Emit our form data on submit for the external application
     * using this package to process it (if it is valid).
     *
     * If there are critical errors, scroll to the first occurance of this.
     * If there are warnings, show warnings dialog.
     */
    handleSubmit(): void {
      const canSubmit = this.form.isValid();

      if (canSubmit) {
        const output = this.form.generateOutput();

        const warnings = this.extractWarnings();

        if (warnings.length === 0) {
          this.$emit('submit', output);
        } else {
          this.$emit('showWarningsDialog', { output, warnings });
        }
      } else {
        const firstErrorField = this.form.state.fields.find((field) => field
          .getErrors()
          .filter((error) => error.level === 'critical').length > 0);

        if (firstErrorField) {
          const elementToScrollTo = firstErrorField.getComponent();
          this.$vuetify.goTo(elementToScrollTo, { offset: 100 });
        }
      }
    },
    /**
     * Emit a save as draft item, the form does not need
     * to be valid in this case.
     */
    handleSaveAsDraft(): void {
      const output = this.form.generateOutput(true);
      this.$emit('saveAsDraft', output);
    },
    /**
     * Assign form meta info to our actual form.
     * We'll also emit an event here in case an external package
     * want to know when the submission date has changed.
     */
    handleFormMetaUpdated(payload) {
      if (!this.formMeta) {
        this.formMeta = {};
      }

      Object.assign(this.formMeta, payload);
      const reportDate = this.form.setDate(this.formMeta);
      this.$emit('reportDateUpdated', reportDate);
      this.form.state.formIsOpen = true;
    },
    /**
     * Listen for changes in the form
     */
    onUpdate(form: FormSubmission) {
      this.$emit('update', form);
    },

    extractWarnings(): Warning[] {
      const groupedWarnings: Record<string, Warning> = {};

      this.form.state.fields.forEach((field) => {
        field.state.errors.forEach((error) => {
          if (error.level === 'warning') {
            if (groupedWarnings[field.id]) {
              groupedWarnings[field.id].messages.push(error.message);
            } else {
              groupedWarnings[field.id] = {
                label: field.label,
                messages: [error.message],
              };
            }
          }
        });
      });

      return Object.values(groupedWarnings);
    },
  },
});
